import { Empty, Flex, Table, type TableColumnsType } from "antd";
import { useTranslation } from "react-i18next";
import { useRecipe } from "../RecipeContext.tsx";
import { QuantityUnitDisplay } from "../../../components/QuantityUnitDisplay.tsx";
import { RecipeIngredientNameCell } from "../RecipeIngredientNameCell.tsx";
import { RecipeCostControls } from "../RecipeCostControls.tsx";
import { useRecipeCostColumn } from "../useRecipeCostColumn.tsx";
import { formatNumber } from "../../../utils/nutritionFormat.ts";
import { noWrap } from "../../../styles/shared.styles.ts";
import type { RecipeDetailFragment } from "../../../gql/generated/graphqlSchema.tsx";

type IngredientRow = RecipeDetailFragment["ingredients"][number];

export const RecipeIngredientsTab = () => {
  const { t, i18n } = useTranslation();
  const { recipe, units } = useRecipe();
  const ingredients = recipe.ingredients;
  const { showPrices, priceColumn, costTotalCell, costModals } = useRecipeCostColumn();

  const gramsUnitId = units.find((u) => u.code === "g")?.id;
  const totalRawWeight = ingredients.reduce((sum, ri) => sum + ri.quantityGrams, 0);
  const weightAfterCooking = recipe.weightAfterCooking;
  const yieldPercent =
    weightAfterCooking != null && totalRawWeight > 0 ? (weightAfterCooking / totalRawWeight) * 100 : null;

  if (ingredients.length === 0) {
    return <Empty description={t("recipes.noIngredients")} />;
  }

  const columns: TableColumnsType<IngredientRow> = [
    {
      title: "#",
      key: "id",
      render: (_, __, index) => index + 1,
      width: 1,
    },
    {
      title: t("recipes.ingredientName"),
      key: "name",
      render: (_, record) => <RecipeIngredientNameCell record={record} />,
      width: "100%",
    },
    {
      title: t("recipes.quantity"),
      key: "quantity",
      onHeaderCell: () => ({ colSpan: 3 }),
      render: (_, record) => <QuantityUnitDisplay quantity={record.quantity} unitId={record.unitId} units={units} />,
    },
    {
      key: "quantityGrams",
      align: "right",
      onHeaderCell: () => ({ colSpan: 0 }),
      render: (_, record) => <QuantityUnitDisplay quantity={record.quantityGrams} unitId={gramsUnitId} units={units} />,
    },
    {
      key: "quantityPercent",
      align: "right",
      onHeaderCell: () => ({ colSpan: 0 }),
      render: (_, record) => <span className={noWrap}>{formatNumber(record.quantityPercent, i18n.language, 1)}%</span>,
    },
  ];
  if (showPrices) {
    columns.push(priceColumn);
  }

  return (
    <Flex gap="middle" vertical>
      <Flex align="center" gap="small">
        <RecipeCostControls />
      </Flex>
      <Table<IngredientRow>
        bordered
        columns={columns}
        dataSource={ingredients}
        pagination={false}
        rowKey="id"
        size="small"
        // eslint-disable-next-line react/no-unstable-nested-components
        summary={() => (
          <>
            <Table.Summary.Row>
              <Table.Summary.Cell colSpan={3} index={0}>
                <strong>{t("recipes.total")}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell align="right" index={3}>
                <strong>
                  <QuantityUnitDisplay quantity={totalRawWeight} unitId={gramsUnitId} units={units} />
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} />
              {showPrices ? costTotalCell(5) : null}
            </Table.Summary.Row>
            {weightAfterCooking != null ? (
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={2} index={0}>
                  <strong>{t("recipes.weightAfterCooking")}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <QuantityUnitDisplay
                    quantity={recipe.weightAfterCookingQuantity}
                    unitId={recipe.weightAfterCookingUnitId}
                    units={units}
                  />
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right" index={3}>
                  <strong>
                    <QuantityUnitDisplay quantity={weightAfterCooking} unitId={gramsUnitId} units={units} />
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} />
                {showPrices ? <Table.Summary.Cell index={5} /> : null}
              </Table.Summary.Row>
            ) : null}
            {yieldPercent != null ? (
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={2} index={0}>
                  <strong>{t("recipes.yield")}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} />
                <Table.Summary.Cell align="right" index={3}>
                  <strong className={noWrap}>{formatNumber(yieldPercent, i18n.language, 1)}%</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} />
                {showPrices ? <Table.Summary.Cell index={5} /> : null}
              </Table.Summary.Row>
            ) : null}
          </>
        )}
      />
      {costModals}
    </Flex>
  );
};
